import { capitalize } from 'lodash';

export enum AbstractSyntaxTreeBuilderRule {
    AllCapitalLettersToHeader,
    LeadingStarToUnorderedList,
    LineBreakToNode,
    RemoveEmptyNode,
    RemoveColonFromHeader,
    TrimContent,
}

export type AbstractSyntaxTreeBuilderNode = {
    children: AbstractSyntaxTreeBuilderNode[];
    key: string;
    original: string;
    type: 'header' | 'paragraph' | 'unordered-list' | 'list-item';
    value: string;
};

export type AbstractSyntaxTreeRenderer<T> = {
    render(nodes: AbstractSyntaxTreeBuilderNode[]): T;
};

export class AbstractSyntaxTreeBuilder<TOutput> {
    public constructor(
        private readonly rendered: AbstractSyntaxTreeRenderer<TOutput>,
        private readonly rules: AbstractSyntaxTreeBuilderRule[] = [
            AbstractSyntaxTreeBuilderRule.AllCapitalLettersToHeader,
            AbstractSyntaxTreeBuilderRule.LeadingStarToUnorderedList,
            AbstractSyntaxTreeBuilderRule.LineBreakToNode,
            AbstractSyntaxTreeBuilderRule.RemoveEmptyNode,
            AbstractSyntaxTreeBuilderRule.RemoveColonFromHeader,
            AbstractSyntaxTreeBuilderRule.TrimContent,
        ]
    ) {}

    public render(content: string | string[]): TOutput {
        return this.rendered.render(this.parse(content));
    }

    public parse(content: string | string[]) {
        const normalizedContent = this.getNormalizedContent(content);
        const nodes: AbstractSyntaxTreeBuilderNode[] = [];
        let key = 0;

        while (normalizedContent.length > 0) {
            key++;
            let value = normalizedContent.shift() as string;
            const original = value;

            const nodeValue = {
                children: [],
                key: key.toString(),
                original,
                type: 'paragraph',
                value,
            };

            if (this.hasRule(AbstractSyntaxTreeBuilderRule.TrimContent)) {
                value = value.trim();
            }

            if (
                this.hasRule(AbstractSyntaxTreeBuilderRule.AllCapitalLettersToHeader) &&
                value === value.toUpperCase()
            ) {
                if (this.hasRule(AbstractSyntaxTreeBuilderRule.RemoveColonFromHeader)) {
                    value = value.endsWith(':') ? value.slice(0, -1) : value;
                }

                nodes.push({
                    ...nodeValue,
                    type: 'header',
                    value: capitalize(value),
                });
            } else if (
                this.hasRule(AbstractSyntaxTreeBuilderRule.LeadingStarToUnorderedList) &&
                value.startsWith('*')
            ) {
                const lastNode = nodes[nodes.length - 1];

                if (lastNode.type !== 'unordered-list') {
                    nodes.push({
                        ...nodeValue,
                        key: key.toString(),
                        original: '',
                        type: 'unordered-list',
                        value: '',
                    });

                    key++;
                }

                (nodes[nodes.length - 1].children as AbstractSyntaxTreeBuilderNode[]).push({
                    ...nodeValue,
                    key: key.toString(),
                    type: 'list-item',
                    value: value.slice(1).trim(),
                });
            } else {
                nodes.push({
                    ...nodeValue,
                    type: 'paragraph',
                    value: value,
                });
            }
        }

        return nodes;
    }

    private getNormalizedContent(content: string | string[]) {
        let output = Array.isArray(content) ? [...content] : [content];

        if (this.hasRule(AbstractSyntaxTreeBuilderRule.LineBreakToNode)) {
            output = output.reduce((result, part) => [...result, ...part.split('\n')], [] as string[]);
        }

        if (this.hasRule(AbstractSyntaxTreeBuilderRule.RemoveEmptyNode)) {
            output = output.filter((part) => part.trim() !== '');
        }

        return output;
    }

    private hasRule(rule: AbstractSyntaxTreeBuilderRule) {
        return this.rules.includes(rule);
    }
}
